import React, { useEffect } from 'react';
import { Box, Flex, Spinner, Text, Icon } from '@chakra-ui/react';
import { FiChevronRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccountsOverview } from '../../hooks/useAccountsOverview';
import AccountsOverviewCard from '../accounts/AccountsOverviewCard';
import { cardStyle, cardTitle, viewAllStyle } from './dashboardStyles';

/**
 * Net worth on the Dashboard: the Accounts page's own assets-and-debts card,
 * on the Accounts page's own load, so its figures are that page's to the
 * cent. It follows the Dashboard's currency tabs and its date filter, in
 * place of the range pills (MI-240). It shows no account's reconciliation
 * status or position, so it reads the shared account list.
 */
export default function DashboardNetWorth({ selectedCurrency, dateRange }) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const {
    loading, currency, setCurrency, availableCurrencies,
    activeAccounts, summary, debtSummary, convertedSummary, convertedIsConverted, displayCurrency,
    trend, chartPeriods, granularity,
  } = useAccountsOverview({
    accountDetails: false,
    period: { startDate: dateRange?.start_date, endDate: dateRange?.end_date },
  });

  useEffect(() => {
    if (selectedCurrency !== 'all' && availableCurrencies.includes(selectedCurrency)) {
      setCurrency(selectedCurrency);
    }
  }, [selectedCurrency, availableCurrencies, setCurrency]);

  // As on the Accounts page: "All" with more than one currency and nothing to
  // convert into shows each currency's figures instead of one chart.
  const multiCurrency = !displayCurrency && selectedCurrency === 'all' && availableCurrencies.length > 1;

  const header = (
    <Flex justify="space-between" align="center" gap={2}>
      <Text {...cardTitle}>{t('netWorth.title')}</Text>
      <Text {...viewAllStyle} onClick={() => navigate('/accounts')}>
        {t('netWorth.viewAll')} <Icon as={FiChevronRight} w="14px" h="14px" />
      </Text>
    </Flex>
  );

  if (loading && !summary) {
    return (
      <Box {...cardStyle}>
        {header}
        <Flex justify="center" align="center" h="220px">
          <Spinner size="sm" color="redesign.text.muted" />
        </Flex>
      </Box>
    );
  }

  return (
    <AccountsOverviewCard
      header={header}
      summary={summary}
      debtSummary={debtSummary}
      converted={convertedSummary}
      convertedIsConverted={convertedIsConverted}
      displayCurrency={displayCurrency}
      multiCurrency={multiCurrency}
      trend={trend}
      currency={currency}
      chartPeriods={chartPeriods}
      granularity={granularity}
      accounts={activeAccounts}
    />
  );
}
